import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import { schemaValidator } from "../models/schemasV2";
import {
  HandleColumnActionParams,
  HandleTableActionParams,
  HandleValueGroupActionParams,
  HandleAttributeValueGroupActionParams,
  Template,
  UseTemplateProps,
  HandleRowKeyActionParams,
  UpdateTemplateParams,
  HandleTableOrderActionParams,
} from "./templateV2/types";
import {
  handleColumnAction,
  handleDeleteTable,
  handleRowKeyAction,
  handleTableAction,
  handleTableOrderAction,
} from "./templateV2/tableColumnActions";
import {
  handleCreateValueGroup,
  handleDeleteValueGroup,
  handleAttributeValueGroupAction,
} from "./templateV2/valueGroupActions";
import { updateSchema } from "./templateV2/schemaActions";

export const useTemplateV2 = ({
  templateId,
  fetchOnLoad,
}: UseTemplateProps) => {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [template, setTemplate] = useState<Template | null>(null);
  const [jsonSchema, setJsonSchema] = useState<string | null>(null);

  const fetchTemplate = async (schemaId: string) => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/schemas/v2/schema/${schemaId}`,
        method: "GET",
      });

      if (response.schema) {
        schemaValidator.parse(response.schema);
        setTemplate(response.schema);
      }
      if (response.json_schema) {
        setJsonSchema(response.json_schema);
      }
    } catch (error) {
      toast.error("An error occurred while fetching template");
      console.error("Error fetching template:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchemaCallback = useCallback(
    (params: UpdateTemplateParams) => updateSchema(setTemplate, params),
    [setTemplate],
  );

  const handleTableActionCallback = useCallback(
    (params: HandleTableActionParams) =>
      handleTableAction(templateId!, setTemplate, params),
    [templateId],
  );

  const handleTableOrderActionCallback = useCallback(
    (params: HandleTableOrderActionParams) =>
      handleTableOrderAction(templateId!, setTemplate, params),
    [templateId],
  );

  const handleColumnActionCallback = useCallback(
    (params: HandleColumnActionParams) =>
      handleColumnAction(templateId!, setTemplate, params),
    [templateId],
  );

  const handleRowKeyActionCallback = useCallback(
    (params: HandleRowKeyActionParams) =>
      handleRowKeyAction(templateId!, setTemplate, params),
    [templateId],
  );

  const handleDeleteTableCallback = useCallback(
    (tableId: string) => handleDeleteTable(templateId!, setTemplate, tableId),
    [templateId],
  );

  const handleCreateValueGroupCallback = useCallback(
    (params: HandleValueGroupActionParams) =>
      handleCreateValueGroup(templateId!, setTemplate, params),
    [templateId],
  );

  const handleDeleteValueGroupCallback = useCallback(
    (valueGroupId: string) =>
      handleDeleteValueGroup(templateId!, setTemplate, valueGroupId),
    [templateId],
  );

  const handleAttributeValueGroupActionCallback = useCallback(
    (params: HandleAttributeValueGroupActionParams) =>
      handleAttributeValueGroupAction(templateId!, setTemplate, params),
    [templateId],
  );

  useEffect(() => {
    if (templateId && fetchOnLoad) {
      fetchTemplate(templateId);
    }
  }, [templateId, fetchOnLoad]);

  return {
    loading,
    template,
    jsonSchema,
    handleUpdateSchema: handleUpdateSchemaCallback,
    handleColumnAction: handleColumnActionCallback,
    handleTableAction: handleTableActionCallback,
    handleTableOrderAction: handleTableOrderActionCallback,
    handleDeleteTable: handleDeleteTableCallback,
    handleCreateValueGroup: handleCreateValueGroupCallback,
    handleDeleteValueGroup: handleDeleteValueGroupCallback,
    handleAttributeValueGroupAction: handleAttributeValueGroupActionCallback,
    handleRowKeyAction: handleRowKeyActionCallback,
    setTemplate,
    fetchTemplate,
  };
};
